/* ============================================
   En-tête Museau & Co, refonte 2026
   Posé le 23-09-2026 sur le brouillon. Voir sections/mc-entete.liquid.

   Quatre choses :
   1. la bascule « détachée » : en haut de page l'en-tête est normal, dès
      qu'on défile il devient une barre de verre détachée (classe
      `mc-entete--detache`), comme Rue des Plantes (rdp-header.js) ;
   2. les panneaux des entrées (Compléments alimentaires, Soin & hygiène,
      Packs ; ordinateur) : mécanique
      reprise telle quelle de Rue des Plantes (rue-des-plantes/theme-dev/
      assets/rdp-mega-menu.js, réglée avec Valentin le 16-09), noms changés ;
   3. le tiroir mobile (sous 1 200 px, relecture n° 1, D1) ;
   4. `--header-height`, que la page panier lit pour son résumé collant.

   Tout est délégué au document : l'éditeur de thème réécrit la section à
   chaque réglage, et des écoutes posées sur des éléments disparus ne se
   rebranchent jamais.

   ─────────────────────────────────────────────────────────────────────────
   LES PANNEAUX : LE SURVOL OUVRE, ET LE CLIC AUSSI

   Deux délais, et pas zéro (Rue des Plantes, 16-09) :
   - OUVERTURE, 120 ms : une souris qui TRAVERSE la barre pour aller ailleurs
     n'ouvre pas deux panneaux au passage ;
   - FERMETURE, 450 ms : le temps de descendre en diagonale vers une carte
     sans perdre le panneau en route. Rentrer dans le panneau annule la
     fermeture en cours.

   Le survol n'existe que pour une souris, `(hover: hover) and (pointer:
   fine)` : sur une tablette, le navigateur simule un survol AU TOUCHER, et
   un menu branché dessus avale le premier toucher.

   Ce qui ferme un panneau : la souris qui quitte le menu (après le délai),
   un second clic sur le déclencheur, Échap (le focus revient sur le
   déclencheur), un clic ailleurs, le focus qui sort de l'entrée au clavier,
   la recherche qui s'ouvre.

   Sans ce fichier, les panneaux restent fermés et chaque déclencheur reste
   un lien qui mène quelque part (la collection de l'entrée) ; le burger,
   lui, ouvre le tiroir par `:target` (relecture n° 1, D12).
   ============================================ */

(function () {
  'use strict';

  // Le script a tourné : la règle `:target` du tiroir (secours sans script,
  // relecture n° 1, D12) ne joue plus. Posé sur <html>, qui survit aux
  // rechargements de section de l'éditeur (relecture n° 2, R1).
  document.documentElement.classList.add('mc-entete-js');

  var OUVERTURE_MS = 120;
  var FERMETURE_MS = 450;
  // Sous 1 200 px : burger et tiroir (relecture n° 1, D1 : la barre
  // complète n'y tient pas, iPad en paysage compris). 1 199,98 et non
  // 1 199 : avec un zoom du navigateur, la largeur peut valoir 1 199,09 px,
  // et aucune des deux tranches ne s'appliquait (relecture n° 2, N7). Même
  // borne que la CSS (assets/mc-entete.css).
  var MOBILE = window.matchMedia('(max-width: 1199.98px)');
  var laSourisSurvole = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var ouvert = null; // le déclencheur du seul panneau ouvert
  var minuterieOuverture = null;
  var minuterieFermeture = null;

  /* Le déclencheur qu'on vient de fermer d'un clic : tant que la souris n'a
     pas quitté l'entrée, le survol ne le rouvre pas (sinon deux pixels de
     mouvement après le clic suffisaient à le rouvrir, relecture RDP du
     16-09). */
  var fermeAuClic = null;

  /* Le panneau ouvert PAR LE SURVOL : le clic qui suit le confirme, il ne
     le ferme pas. Sans ça, la cliente qui pose la souris puis clique comme
     elle en a l'habitude refermait le panneau (relecture RDP, gravité 4). */
  var ouvertAuSurvol = null;

  function annulerLesMinuteries() {
    clearTimeout(minuterieOuverture);
    clearTimeout(minuterieFermeture);
    minuterieOuverture = null;
    minuterieFermeture = null;
  }

  function panneauDe(declencheur) {
    var id = declencheur.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function rechercheOuverte() {
    return !!document.querySelector('.mc-entete__recherche details[open]');
  }

  function fermer(declencheur, rendreLeFocus) {
    if (!declencheur) return;
    var panneau = panneauDe(declencheur);
    declencheur.setAttribute('aria-expanded', 'false');
    if (panneau) panneau.hidden = true;
    if (ouvert === declencheur) ouvert = null;
    if (ouvertAuSurvol === declencheur) ouvertAuSurvol = null;
    if (rendreLeFocus) declencheur.focus();
    // La barre a pu garder son état pendant que le panneau était ouvert
    // (voir « La bascule détachée ») : elle le rattrape maintenant.
    planifierDetache();
  }

  function ouvrir(declencheur) {
    // Un seul panneau à la fois : ils occupent la même place sous la barre.
    if (ouvert && ouvert !== declencheur) fermer(ouvert, false);
    var panneau = panneauDe(declencheur);
    declencheur.setAttribute('aria-expanded', 'true');
    if (panneau) {
      panneau.hidden = false;
      // Le plafond de hauteur se calcule sur la position RÉELLE du panneau :
      // la barre descend quand le bandeau d'annonce est encore à l'écran.
      panneau.style.setProperty('--mc-e-panneau-haut', Math.round(panneau.getBoundingClientRect().top) + 'px');
    }
    ouvert = declencheur;
  }

  function basculer(declencheur) {
    annulerLesMinuteries();
    if (declencheur.getAttribute('aria-expanded') === 'true' && ouvertAuSurvol === declencheur) {
      ouvertAuSurvol = null;
      return;
    }
    ouvertAuSurvol = null;
    if (declencheur.getAttribute('aria-expanded') === 'true') {
      fermer(declencheur, false);
      fermeAuClic = declencheur;
    } else {
      fermeAuClic = null;
      ouvrir(declencheur);
    }
  }

  function programmerFermeture() {
    clearTimeout(minuterieFermeture);
    minuterieFermeture = setTimeout(function () {
      if (ouvert) fermer(ouvert, false);
    }, FERMETURE_MS);
  }

  /* ── Le survol ───────────────────────────────────────────────────── */

  if (laSourisSurvole) {
    document.addEventListener('mouseover', function (evenement) {
      var cible = evenement.target;
      if (!cible.closest || !cible.closest('.mc-entete__nav')) return;

      // Revenue dans le menu : la fermeture en cours est annulée.
      clearTimeout(minuterieFermeture);
      minuterieFermeture = null;

      // Dans le panneau ouvert : rien d'autre, surtout pas reprogrammer une
      // ouverture à chaque carte traversée.
      if (cible.closest('.mc-entete__panneau')) return;

      var entree = cible.closest('.mc-entete__entree');
      if (!entree) return;

      if (fermeAuClic && !entree.contains(fermeAuClic)) fermeAuClic = null;

      var declencheur = entree.querySelector('[data-mc-e-declencheur]');
      if (!declencheur) {
        // Une entrée sans panneau (un simple lien) : la survoler ferme le
        // panneau ouvert, sinon il reste par-dessus là où l'on essaie de
        // cliquer.
        clearTimeout(minuterieOuverture);
        minuterieOuverture = null;
        if (ouvert) programmerFermeture();
        return;
      }

      if (ouvert === declencheur) return;
      // La recherche est ouverte : un pointeur qui passe ne rouvre rien
      // par-dessus. Le clic, lui, ouvre toujours.
      if (rechercheOuverte()) return;
      if (fermeAuClic === declencheur) return;

      clearTimeout(minuterieOuverture);
      minuterieOuverture = setTimeout(function () {
        ouvrir(declencheur);
        ouvertAuSurvol = declencheur;
      }, OUVERTURE_MS);
    });

    document.addEventListener('mouseout', function (evenement) {
      var cible = evenement.target;
      if (!cible.closest || !cible.closest('.mc-entete__nav')) return;
      // `relatedTarget` : l'élément où la souris ARRIVE. Encore dans le
      // menu, c'est un déplacement interne.
      var arrivee = evenement.relatedTarget;
      if (arrivee && arrivee.closest && arrivee.closest('.mc-entete__nav')) return;

      fermeAuClic = null;
      clearTimeout(minuterieOuverture);
      minuterieOuverture = null;
      if (ouvert) programmerFermeture();
    });
  }

  /* ── Le clic, le clavier ─────────────────────────────────────────── */

  document.addEventListener('click', function (evenement) {
    var cible = evenement.target;
    if (!cible.closest) return;

    var declencheur = cible.closest('[data-mc-e-declencheur]');
    // Cmd, Ctrl ou Maj + clic : ouvrir la collection dans un onglet, comme
    // tout lien. Le déclencheur est un <a> justement pour mener quelque part.
    if (declencheur && (evenement.metaKey || evenement.ctrlKey || evenement.shiftKey)) return;
    if (declencheur) {
      evenement.preventDefault();
      basculer(declencheur);
      return;
    }

    // Un clic ailleurs ferme, sauf DANS le panneau ouvert : sinon il se
    // refermerait avant que le navigateur ne suive le lien.
    if (ouvert && !cible.closest('.mc-entete__panneau')) {
      annulerLesMinuteries();
      fermer(ouvert, false);
    }
  });

  document.addEventListener('keydown', function (evenement) {
    // Le déclencheur est un <a> : la barre d'espace ferait défiler la page
    // au lieu de l'activer. Entrée, elle, déclenche un clic nativement.
    if (evenement.key === ' ' || evenement.key === 'Spacebar') {
      // Le burger aussi est un lien (secours sans script, D12) : la barre
      // d'espace doit l'actionner comme un bouton.
      var burgerVise = evenement.target.closest && evenement.target.closest('[data-mc-e-burger]');
      if (burgerVise) {
        evenement.preventDefault();
        if (tiroirOuvert()) fermerTiroir(false);
        else ouvrirTiroir();
        return;
      }
      var cible = evenement.target.closest && evenement.target.closest('[data-mc-e-declencheur]');
      if (cible) {
        evenement.preventDefault();
        basculer(cible);
        return;
      }
    }

    if (evenement.key === 'Escape' && ouvert) {
      annulerLesMinuteries();
      fermer(ouvert, true);
    }
  });

  // Le focus qui sort de l'ENTRÉE ouverte (son déclencheur et son panneau)
  // ferme le panneau : Tab depuis la dernière carte de « Nos produits » vers
  // « Packs » ne laisse pas le premier panneau ouvert sous le second.
  // `focusin` sur le document plutôt que `focusout` : `focusout` part AVANT
  // que le nouvel élément ait le focus, et son `relatedTarget` est nul dans
  // plusieurs navigateurs.
  document.addEventListener('focusin', function (evenement) {
    if (!ouvert) return;
    var entree = ouvert.closest('.mc-entete__entree');
    if (entree && entree.contains(evenement.target)) return;
    // Le focus part dans le panier tiroir qui s'ouvre (il le prend AVANT
    // que l'observation de sa classe `active` ne passe, plus bas) : c'est
    // l'ouverture du panier qui ferme le panneau, et lui confie le
    // déclencheur pour y rendre le focus (relecture n° 2, N1).
    var panier = evenement.target.closest && evenement.target.closest('cart-drawer');
    if (panier) {
      surPanierOuvert(panier);
      return;
    }
    annulerLesMinuteries();
    fermer(ouvert, false);
  });

  /* ============================================
     Le tiroir mobile
     ============================================ */

  function tiroir() {
    return document.querySelector('[data-mc-e-tiroir]');
  }

  function burger() {
    return document.querySelector('[data-mc-e-burger]');
  }

  function voile() {
    return document.querySelector('[data-mc-e-voile]');
  }

  // Le tiroir et son voile glissent et fondent par des CLASSES
  // (`est-ouvert`, `est-visible`) : le glissé de 300 ms
  // (assets/mc-entete.css) a besoin d'un état fermé MIS EN PAGE pour partir.
  // Le tiroir porte en plus `hidden` une fois fermé (relecture n° 1, D3 :
  // sinon ses 15 images se téléchargeaient à chaque page) : on le retire
  // juste avant d'ouvrir, on le remet quand le glissé de fermeture est fini.
  var minuterieTiroir = null;

  function tiroirOuvert() {
    var t = tiroir();
    return !!t && t.classList.contains('est-ouvert');
  }

  function montrerVoile(oui) {
    var v = voile();
    if (v) v.classList.toggle('est-visible', oui);
  }

  function libellerBurger(b, estOuvert) {
    var texte = b.querySelector('[data-mc-e-burger-texte]');
    if (texte) texte.textContent = texte.getAttribute(estOuvert ? 'data-fermer' : 'data-ouvrir');
  }

  function focusables() {
    var t = tiroir();
    var b = burger();
    if (!t) return [];
    var liste = [].slice.call(
      t.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return el.getClientRects().length > 0 && !el.closest('[hidden]') && !el.classList.contains('hidden');
    });
    if (b) liste.unshift(b);
    return liste;
  }

  function ouvrirTiroir() {
    var t = tiroir();
    var b = burger();
    if (!t || !b) return;
    if (ouvert) fermer(ouvert, false);
    // Le tiroir se pose sous le VERRE, là où il est vraiment : plus bas
    // tant que le bandeau d'annonce est à l'écran, et, une fois la barre
    // détachée, au bas de la forme de verre (la boîte de la barre, elle,
    // garde sa hauteur).
    var barre = b.closest('.mc-entete__barre');
    if (barre) {
      var retraitBas = parseFloat(getComputedStyle(barre, '::before').bottom) || 0;
      var bas = Math.round(barre.getBoundingClientRect().bottom - retraitBas) + 8;
      t.style.setProperty('--mc-e-tiroir-haut', bas + 'px');
    }
    // Fermé, le tiroir porte `hidden` (rien n'est téléchargé, D3) : on le
    // rend, on force une mise en page pour que le glissé parte de l'état
    // fermé, puis on l'ouvre.
    clearTimeout(minuterieTiroir);
    t.hidden = false;
    void t.offsetWidth;
    t.classList.add('est-ouvert');
    montrerVoile(true);
    b.setAttribute('aria-expanded', 'true');
    libellerBurger(b, true);
    document.body.classList.add('overflow-hidden');
    // Le focus va au tiroir lui-même, pas au champ de recherche : sur un
    // téléphone, un champ qui prend le focus ouvre le clavier sans qu'on
    // l'ait demandé.
    t.focus({ preventScroll: true });
  }

  function fermerTiroir(rendreLeFocus) {
    var t = tiroir();
    var b = burger();
    if (!t || !tiroirOuvert()) return;
    t.classList.remove('est-ouvert');
    // Remasqué une fois le glissé fini (0 ms si le système demande moins
    // d'animations : la transition vaut alors 0 s).
    var duree = (parseFloat(getComputedStyle(t).transitionDuration) || 0) * 1000;
    clearTimeout(minuterieTiroir);
    minuterieTiroir = setTimeout(function () {
      if (!t.classList.contains('est-ouvert')) t.hidden = true;
    }, duree + 20);
    if (!rechercheOuverte()) montrerVoile(false);
    if (b) {
      b.setAttribute('aria-expanded', 'false');
      libellerBurger(b, false);
      if (rendreLeFocus) b.focus({ preventScroll: true });
    }
    // Le verrou de la page n'est pas à nous s'il est au panier tiroir.
    if (!rechercheOuverte() && !document.querySelector('cart-drawer.active')) document.body.classList.remove('overflow-hidden');
  }

  document.addEventListener('click', function (evenement) {
    var cible = evenement.target;
    if (!cible.closest) return;

    var b = cible.closest('[data-mc-e-burger]');
    if (b) {
      evenement.preventDefault();
      if (tiroirOuvert()) fermerTiroir(false);
      else ouvrirTiroir();
      return;
    }

    // Le voile : ferme le tiroir. (Pour la recherche, c'est details-modal.js
    // qui ferme, un clic hors de la fenêtre suffit.)
    if (cible.closest('[data-mc-e-voile]') && tiroirOuvert()) {
      fermerTiroir(true);
      return;
    }

    // Un lien du tiroir : on ferme avant de partir. Utile surtout pour une
    // ancre de la page en cours, qui ferait défiler la page DERRIÈRE un
    // tiroir resté ouvert (relecture RDP n° 3).
    if (cible.closest('[data-mc-e-tiroir] a[href]') && tiroirOuvert()) {
      fermerTiroir(false);
    }
  });

  // 🔴 La gamelle reste touchable au-dessus du voile. Ouvrir le panier
  // tiroir depuis le menu ouvert faisait tourner DEUX pièges à focus, le
  // premier Échap fermait les deux calques, et fermer le panier libérait la
  // page derrière le menu resté ouvert (relecture n° 1, D4). On ferme donc
  // le menu AVANT que le panier ne s'ouvre : écoute en capture, qui passe
  // avant celle que cart-drawer.js pose sur la gamelle.
  document.addEventListener(
    'click',
    function (evenement) {
      var cible = evenement.target;
      if (!cible.closest || !cible.closest('#cart-icon-bubble')) return;
      if (tiroirOuvert()) fermerTiroir(false);
    },
    true
  );

  document.addEventListener('keydown', function (evenement) {
    if (!tiroirOuvert()) return;
    if (evenement.key === 'Escape') {
      // Des suggestions de recherche ouvertes : Échap les ferme d'abord (le
      // script de Dawn, lui, ne réagit pas à Échap) ; le suivant ferme le
      // tiroir.
      evenement.preventDefault();
      var suggestions = document.querySelector('[data-mc-e-tiroir] predictive-search[open]');
      if (suggestions && typeof suggestions.close === 'function') {
        suggestions.close();
        return;
      }
      fermerTiroir(true);
      return;
    }
    if (evenement.key !== 'Tab') return;
    // Le focus boucle sur le bouton du menu et le tiroir, dans cet ordre.
    // Tout est conduit à la main : le burger est le PREMIER élément de la
    // barre, avant le logo (23-09), et l'ordre du document enverrait le Tab
    // suivant sur le logo, hors du tiroir (relevé au banc).
    var liste = focusables();
    if (!liste.length) return;
    evenement.preventDefault();
    var actif = document.activeElement;
    var rang = liste.indexOf(actif);
    if (rang === -1) {
      // Le tiroir lui-même (focus à l'ouverture) ou un élément égaré.
      liste[evenement.shiftKey ? liste.length - 1 : 1 % liste.length].focus();
      return;
    }
    var suivant = (rang + (evenement.shiftKey ? -1 : 1) + liste.length) % liste.length;
    liste[suivant].focus();
  });

  // Passé à 1 200 px ou plus (tablette tournée, fenêtre élargie) : le
  // tiroir n'a plus de bouton pour se fermer, on le ferme.
  function surChangementDeLargeur(evenement) {
    if (!evenement.matches && tiroirOuvert()) fermerTiroir(false);
    if (evenement.matches && ouvert) fermer(ouvert, false);
  }
  // Le burger touché AVANT que ce script différé ne tourne : le lien a posé
  // #McEnteteTiroirAncre dans l'adresse et le tiroir s'est affiché par
  // `:target` (snippets/mc-entete-tiroir.liquid). On reprend la main :
  // adresse nettoyée, tiroir ouvert pour de bon (il pourra se fermer).
  if (window.location.hash === '#McEnteteTiroirAncre' || window.location.hash === '#McEnteteTiroir') {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    if (MOBILE.matches) ouvrirTiroir();
  }

  if (MOBILE.addEventListener) MOBILE.addEventListener('change', surChangementDeLargeur);
  else if (MOBILE.addListener) MOBILE.addListener(surChangementDeLargeur);

  /* ============================================
     La recherche : son voile, et un calque à la fois
     ============================================ */

  // `toggle` ne remonte pas : écouté en capture.
  document.addEventListener(
    'toggle',
    function (evenement) {
      var details = evenement.target;
      if (!details.closest || !details.closest('.mc-entete__recherche')) return;
      if (details.open) {
        if (ouvert) {
          annulerLesMinuteries();
          fermer(ouvert, false);
        }
        if (tiroirOuvert()) fermerTiroir(false);
        montrerVoile(true);
      } else if (!tiroirOuvert()) {
        montrerVoile(false);
      }
    },
    true
  );

  /* ============================================
     Le panier tiroir s'ouvre : un calque à la fois
     ============================================ */

  // 🔴 Relecture n° 2, N1 (suite de D4). Le panier tiroir s'ouvre par
  // plusieurs chemins : la gamelle (cart-drawer.js), la réponse d'un ajout
  // au panier (product-form.js → renderContents() → open() : fiche produit,
  // bouton collant du pilote Immunité, ajout rapide), un ajout refusé
  // (mc-panier.js → open()), ou l'open() d'une app. Tous finissent par la
  // classe `active` sur <cart-drawer> : c'est CET état qu'on observe, pas
  // un déclencheur. Dès qu'il paraît, le menu, le panneau et la recherche
  // se ferment. Sinon deux pièges à focus tournaient ensemble (le Tab
  // restait sur « Fermer le panier »), un seul Échap fermait les deux, et
  // la croix du panier libérait la page derrière le menu resté ouvert.
  // Aucun changement dans cart-drawer.js ni mc-panier.js (chantier du
  // panier) : on lit leur état, on ne le modifie pas.
  var panierObserve = null;
  var panierEtaitActif = false;

  function surPanierOuvert(panier) {
    // L'élément de la barre qui recevra le focus à la fermeture du panier,
    // si celui qui l'a ouvert est dans un calque qu'on ferme.
    var retour = null;
    if (tiroirOuvert()) {
      fermerTiroir(false);
      retour = burger();
    }
    if (ouvert) {
      var declencheurOuvert = ouvert;
      annulerLesMinuteries();
      fermer(ouvert, false);
      retour = retour || declencheurOuvert;
    }
    var recherche = document.querySelector('.mc-entete__recherche details[open]');
    if (recherche) {
      var modale = recherche.closest('details-modal');
      if (modale && typeof modale.close === 'function') modale.close(false);
      else recherche.removeAttribute('open');
      // details-modal.js retire le verrou de la page en se fermant : il est
      // au panier, on le lui rend.
      document.body.classList.add('overflow-hidden');
      retour = retour || recherche.querySelector('summary');
    }
    // cart-drawer.js rend le focus, à sa fermeture, à ce qui l'a ouvert :
    // rien, ou un élément du calque qu'on vient de masquer, et le focus se
    // perdait. On lui donne l'élément visible de la barre.
    if (retour && typeof panier.setActiveElement === 'function') {
      var actif = panier.activeElement;
      var perdu = !actif || !document.contains(actif) ||
        (actif.closest && !!actif.closest('[data-mc-e-tiroir], [data-mc-e-panneau], .mc-entete__recherche details'));
      if (perdu) panier.setActiveElement(retour);
    }
  }

  var observateurPanier = window.MutationObserver
    ? new MutationObserver(function () {
        var actif = !!panierObserve && panierObserve.classList.contains('active');
        if (actif && !panierEtaitActif) surPanierOuvert(panierObserve);
        panierEtaitActif = actif;
      })
    : null;

  function observerPanier() {
    var panier = document.querySelector('cart-drawer');
    if (!observateurPanier || !panier || panier === panierObserve) return;
    observateurPanier.disconnect();
    panierObserve = panier;
    panierEtaitActif = panier.classList.contains('active');
    observateurPanier.observe(panier, { attributes: true, attributeFilter: ['class'] });
  }

  observerPanier();

  // Toucher prolongé sur le burger, un lien depuis D12 : pas de menu
  // « Ouvrir dans un nouvel onglet » (Android). Pour iOS : CSS
  // (-webkit-touch-callout). Relecture n° 2, R2.
  document.addEventListener('contextmenu', function (evenement) {
    var cible = evenement.target;
    if (cible.closest && cible.closest('[data-mc-e-burger]')) evenement.preventDefault();
  });

  /* ============================================
     La bascule « détachée »
     Reprise de Rue des Plantes (rdp-header.js, 15-09) : au premier pixel
     défilé, pas après la hauteur de l'en-tête. Seuil à 4 px et non 0 : sur
     certains navigateurs mobiles, le rebond de fin de page laisse un scrollY
     de 1 ou 2 au repos, et la barre clignoterait.
     ============================================ */

  var SEUIL_DETACHE = 4;
  var detacheEnAttente = false;

  // 🔴 Un panneau OUVERT fige la barre dans son état (relecture n° 2, N2) :
  // la molette sur un panneau ouvert en haut de page (permise depuis D14)
  // faisait basculer la barre, et le panneau SAUTAIT sous le pointeur
  // (36 px à gauche, 72 px plus large, 43 px plus haut) en passant du blanc
  // au verre d'un coup ; une carte arrivait sous la souris. La barre suit la
  // page à la fermeture du panneau (`fermer` rappelle cette bascule), avec
  // son glissé habituel.
  function appliquerDetache() {
    detacheEnAttente = false;
    var entete = document.querySelector('[data-mc-e-entete]');
    if (!entete) return;
    if (ouvert) return;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    entete.classList.toggle('mc-entete--detache', y > SEUIL_DETACHE);
  }

  function planifierDetache() {
    if (detacheEnAttente) return;
    detacheEnAttente = true;
    window.requestAnimationFrame(appliquerDetache);
  }

  appliquerDetache();
  window.addEventListener('scroll', planifierDetache, { passive: true });

  /* ============================================
     --header-height
     ============================================ */

  function poserHauteur() {
    var section = document.querySelector('.mc-entete-section');
    if (!section) return;
    document.documentElement.style.setProperty('--header-height', section.offsetHeight + 'px');
  }

  poserHauteur();
  window.addEventListener('resize', poserHauteur, { passive: true });
  if (window.ResizeObserver) {
    var section = document.querySelector('.mc-entete-section');
    if (section) new ResizeObserver(poserHauteur).observe(section);
  }

  /* ============================================
     L'éditeur de thème
     ============================================ */

  // Une section rechargée sans recharger la page : on repart d'un état
  // propre, sinon une minuterie en vol fermerait un panneau disparu.
  document.addEventListener('shopify:section:load', function () {
    annulerLesMinuteries();
    ouvert = null;
    ouvertAuSurvol = null;
    fermeAuClic = null;
    document.body.classList.remove('overflow-hidden');
    poserHauteur();
    appliquerDetache();
    // La nouvelle gamelle rendue par l'éditeur n'a plus l'écoute qui ouvre
    // le panier tiroir (cart-drawer.js ne la pose qu'une fois, au
    // chargement) : sans ça, elle menait à /cart (relecture n° 1, D16).
    var panier = document.querySelector('cart-drawer');
    if (panier && typeof panier.setHeaderCartIconAccessibility === 'function') panier.setHeaderCartIconAccessibility();
    observerPanier();
  });

  // Un bloc choisi dans l'éditeur (un besoin, un pack…) : on ouvre le
  // panneau qui le contient, pour que Valentin voie ce qu'il
  // règle, son icône comprise.
  document.addEventListener('shopify:block:select', function (evenement) {
    var bloc = evenement.target;
    if (!bloc || !bloc.closest) return;
    // Aperçu de l'éditeur sous 1 200 px (souvent le cas sur un portable,
    // relecture n° 2) : pas de panneau, on ouvre le tiroir sur la catégorie
    // de l'entrée choisie, pour qu'elle se voie.
    var entreeChoisie = bloc.getAttribute && bloc.getAttribute('data-mc-e-collection');
    if (MOBILE.matches) {
      if (!entreeChoisie) return;
      if (!tiroirOuvert()) ouvrirTiroir();
      var categorie = document.querySelector('[data-mc-e-tiroir] [data-mc-e-collection="' + entreeChoisie + '"]');
      if (categorie) categorie.scrollIntoView({ block: 'nearest' });
      return;
    }
    // Un bloc « Entrée » choisi : son propre panneau.
    var declencheur = bloc.querySelector && bloc.querySelector('[data-mc-e-declencheur]');
    var panneau = declencheur ? panneauDe(declencheur) : bloc.closest('[data-mc-e-panneau]');
    if (panneau && !MOBILE.matches) {
      declencheur = document.querySelector('[aria-controls="' + panneau.id + '"]');
      if (declencheur) {
        annulerLesMinuteries();
        ouvrir(declencheur);
      }
    }
  });

  document.addEventListener('shopify:block:deselect', function () {
    if (ouvert) fermer(ouvert, false);
    if (tiroirOuvert()) fermerTiroir(false);
  });
})();
